/* eslint-disable react/jsx-key */
import React, { useEffect, useMemo, useRef } from 'react';

import { TriangleDownIcon, TriangleUpIcon } from '@chakra-ui/icons';
import {
  Flex,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  Box,
  CircularProgress,
  useDisclosure,
  InputGroup,
  InputLeftAddon,
  Input,
  HStack,
} from '@chakra-ui/react';
import { format } from 'date-fns';
import { isEmpty } from 'ramda';
import { useHistory } from 'react-router-dom';
import { useTable, useSortBy } from 'react-table';

import CharityAddNew from 'components/modules/CharityAddNew/CharityAddNew.view';
import CharityDetails from 'components/modules/CharityDetails';
import Button from 'components/primitives/Button';
import PaginationView from 'components/primitives/Pagination';
import { Listing, Search } from 'components/primitives/SVG';
import Typography from 'components/primitives/Typography';
import { routesSettings } from 'constants/routes';
import { CharitiesGeneratedProps } from 'routes/main/Charities/Charities.props';
import ucFirst from 'utils/string/ucFirst';

const widthPerAccessor = {
  undefined: undefined,
};

const CharitiesView = ({
  data,
  meta,
  onSearchKey,
  isLoading,
  setCharity,
  tableOptions,
  setTableOptions,
  refetch,
}: CharitiesGeneratedProps): JSX.Element => {
  const history = useHistory();
  const searchRef = useRef() as React.MutableRefObject<HTMLInputElement>;
  const addNewModal = useDisclosure();

  const query = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  );

  const onViewCharity = (id: string) => {
    history.push(`${routesSettings.MAIN_CHARITIES.path}?id=${id}`);
  };

  const id = useMemo(() => query.get('id'), [location.search]);

  const columns = React.useMemo(
    () => [
      {
        Header: 'Name',
        accessor: 'name',
      },
      {
        Header: 'Priority',
        accessor: 'priority',
      },
      {
        Header: 'Donations',
        accessor: 'donationsCount',
      },
      {
        Header: 'Total Amount',
        accessor: 'donationsTotalAmount',
        Cell: ({ row }: any) => `NZD ${row.original.donationsTotalAmount}`,
      },
      {
        Header: 'Created By',
        accessor: 'createdBy',
        Cell: ({ row }: any) => row.original.createdBy.name,
      },
      {
        Header: 'Date Created',
        accessor: 'createdAt',
        Cell: ({ row }: any) =>
          row.original.createdAt
            ? format(new Date(row.original.createdAt), 'MM/dd/y kk:mm:ss a')
            : '-',
      },
      {
        Header: 'Status',
        accessor: 'status',
        Cell: ({ row }: any) => ucFirst(row.original.status),
      },
    ],
    []
  );

  const currentIndex = useMemo(
    () => (id ? data.findIndex((d) => d.id === id) : -1),
    [data, id]
  );

  const onPrev = () => {
    if (currentIndex > 0) setCharity(data[currentIndex - 1]);
  };

  const onNext = () => {
    if (currentIndex < data.length - 1) {
      setCharity(data[currentIndex + 1]);
    }
  };

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    state: { sortBy },
  } = useTable(
    {
      data,
      // @ts-ignore
      columns,
      manualSortBy: true,
      disableMultiSort: true,
      disableSortBy: isLoading,
    },
    useSortBy
  );

  const onPaginate = (page: number, pageSize: number) => {
    setTableOptions((prevState) => {
      return {
        ...prevState,
        page: prevState.limit !== pageSize ? 1 : page,
        limit: pageSize,
      };
    });
  };

  useEffect(() => {
    setTableOptions((prevState) => {
      if (!isEmpty(sortBy)) {
        const { id, desc } = sortBy[0];
        return {
          ...prevState,
          sort: id,
          direction: desc ? 'desc' : 'asc',
        };
      } else if (isEmpty(sortBy) && !!prevState.sort) {
        const psCopy = { ...prevState };
        delete psCopy.sort;
        delete psCopy.direction;

        return psCopy;
      }

      return prevState;
    });
  }, [sortBy]);

  return (
    <Flex flexDirection="column">
      <Flex flex={1} justifyContent="space-between">
        <Typography variant="type5" weight="700" color="primary">
          CHARITIES
        </Typography>
        <Button variant="small" onClick={addNewModal.onOpen}>
          Create New Charity
        </Button>
      </Flex>
      <Flex
        mt={5}
        flex={1}
        borderRadius={10}
        p={5}
        minHeight="500px"
        bg="#fff"
        filter="drop-shadow(0px 2px 10px rgba(149, 149, 149, 0.18))"
        flexDirection="column"
      >
        <Flex
          flexDir="row"
          width="100%"
          justifyContent="space-between"
          alignItems="flex-start"
        >
          <Flex height="30px" alignItems="center">
            <Listing color="shade10" width={12} height={12} />
            <Flex mx={3}>
              <Typography variant="type8" weight="600" color="shade10">
                ALL CHARITIES
              </Typography>
            </Flex>
            {isLoading ? (
              <CircularProgress isIndeterminate size="20px" color="primary" />
            ) : (
              <Typography variant="type10" color="shade5">
                {meta.count} {meta.count > 1 ? 'charities' : 'charity'}
              </Typography>
            )}
          </Flex>
          <HStack spacing={5}>
            <InputGroup size="sm" width={300}>
              <InputLeftAddon borderRadius={10} backgroundColor="transparent">
                <Search width="14px" height="14px" />
              </InputLeftAddon>
              <Input
                ref={searchRef}
                onChange={(a) => onSearchKey(a.target.value)}
                borderLeft="none"
                placeholder="Search"
                borderRadius={10}
              />
            </InputGroup>
          </HStack>
        </Flex>
        <Table size="sm" mt={7} {...getTableProps()}>
          <Thead backgroundColor="shade12">
            {headerGroups.map((headerGroup) => (
              <Tr {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map((column) => (
                  <Th
                    {
                      // @ts-ignore
                      ...column.getHeaderProps(column.getSortByToggleProps())
                    }
                  >
                    <Flex flexDirection="row">
                      <Typography variant="type9" weight="600" color="shade5">
                        {column.Header}
                      </Typography>
                      <Flex ml={2}>
                        {
                          // @ts-ignore
                          column.isSorted ? (
                            <Box>
                              {
                                // @ts-ignore
                                column.isSortedDesc ? (
                                  <TriangleDownIcon aria-label="sorted descending" />
                                ) : (
                                  <TriangleUpIcon aria-label="sorted ascending" />
                                )
                              }
                            </Box>
                          ) : (
                            <Box width="12px" height="5px" />
                          )
                        }
                      </Flex>
                    </Flex>
                  </Th>
                ))}
              </Tr>
            ))}
          </Thead>
          <Tbody {...getTableBodyProps()}>
            {rows.map((row, i) => {
              prepareRow(row);
              return (
                <Tr
                  backgroundColor={i % 2 !== 0 ? 'shade18' : 'transparent'}
                  cursor="pointer"
                  onClick={() => {
                    onViewCharity(row.original.id);
                  }}
                  {...row.getRowProps()}
                >
                  {row.cells.map((cell) => (
                    <Td
                      {...cell.getCellProps()}
                      // @ts-ignore
                      width={widthPerAccessor[cell.column.id]}
                    >
                      <Typography variant="type8" color="shade5">
                        {cell.render('Cell')}
                      </Typography>
                    </Td>
                  ))}
                </Tr>
              );
            })}
          </Tbody>
        </Table>

        <CharityDetails
          id={id || undefined}
          onClose={() => {
            setCharity(undefined);
          }}
          onNext={onNext}
          onPrev={onPrev}
          isOpen={Boolean(id)}
          disablePrev={currentIndex === 0}
          disableNext={currentIndex === data.length - 1}
        />

        <CharityAddNew {...addNewModal} refetch={refetch} />

        <Flex mt={5} flexDir="row" width="100%" justifyContent="flex-end">
          <PaginationView
            disabled={isLoading}
            total={meta.count}
            current={meta.page}
            pageSize={tableOptions.limit}
            onChange={onPaginate}
          />
        </Flex>
      </Flex>
    </Flex>
  );
};

export default CharitiesView;
